import PropTypes from 'prop-types';
import {formValueSelector, reduxForm} from 'redux-form';
import useLabels from 'infrastructure/js/hooks/useLabels';
import Validation from 'infrastructure/js/components/controls/controlsValidations.js';
import Dialog from 'infrastructure/js/components/Dialog/dialog';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import EntitiesMultiSelect from '../../Common/Controls/EntitiesMultiSelect/entitiesMultiSelect';
import {enumTypes, getEnumLabel, getEnumValue} from '../../../utils/enumHelper';
import {EntityPropertyTypes} from '../../../enums/entityPropertyTypes';
import {FetchEntitiesFilters} from '../../../enums/fetchEntitiesFilters';
import Combobox from 'infrastructure/js/components/controls/Combobox/combobox';
import List from 'infrastructure/js/components/List/list';
import GenerateButton from 'infrastructure/js/components/controls/GenerateButton/generateButton';
import UnitHelper, {unitTypes} from 'infrastructure/js/utils/uomHelper';
import {useEffect, useState} from 'react';
import TextAreaField from 'infrastructure/js/components/controls/TextAreaField/textAreaField';
import {getAssetStatusesOptions, round} from '../../../utils/assetHelper';
import {connect} from 'react-redux';
import AssetLengthListItem from '../../Common/Controls/AddRemoveList/AddRemoveListItem/AssetLengthListItem/assetLengthListItem';
import * as dialogHelper from 'infrastructure/js/components/Dialog/dialogHelper';

import './createSpoolFromCassettesDialog.scss';

const getStatuses = () => {
  return getAssetStatusesOptions();
}

const getInitialCassettes = (items = []) => {
  return items.map((item) => {
    return {
      asset: {
        assetsType: {
          value: getEnumValue(enumTypes.OBJECT_TYPE)('CASSETTE'),
          label: getEnumLabel(enumTypes.OBJECT_TYPE)('CASSETTE'),
        },
        assetsValue: {
          value: item.id,
          label: item.businessId,
          data: item
        }
      },
      length: item.lengthLeft ? UnitHelper.serverValueToUserValue(unitTypes.LENGTH, item.lengthLeft, 2) : 0,
    }
  });
}

const getComponentToRender = (props) => {
  if (!props?.data ) {
    return null;
  }
  let option = props.data;
  return <span className={option.isDisabled ? 'sub-rolls-status-title' : 'sub-rolls-status'}>{option.label}</span>
}

const createSourceCassettes = (items = []) => {
  return items.map(item => {
    return (
      {
        cassetteId: item?.asset?.assetsValue?.data?.id,
        lengthToConsume: UnitHelper.userValueToServerValue(unitTypes.LENGTH, +(item?.length) ?? 0, 2)
      });
  });
}

const maxLength100 = Validation.maxLength(100);

const WO_ENTITIES_TYPES = [getEnumValue(enumTypes.OBJECT_TYPE)('WO')];

const WO_FETCH_CONFIG = {
  entityType: EntityPropertyTypes.WO_BUSINESS_ID,
  filter: [FetchEntitiesFilters.ACTIVE],
};

export function CreateSpoolFromCassettesDialog( props ) {

  const {
    actions,
    fetchEntitiesActions,
    sData,
    handleSubmit,
    change,
    touch,
    initialize,
    pristine = false,
    reloadParentComponent = null,
    fieldsValues = [],
  ...others} = props;

  const dialogLabels = useLabels('mat.dialog.');
  const labels = useLabels('mat.locationPage.view.spooling.createSpoolFromCassettes.dialog.');
  const [printPdfLabels, setPrintPdfLabels] = useState(false);

  const footerCheckbox = {
    text: labels.get('footer.printPdfLabels'),
    action: (value) => {
      setPrintPdfLabels(value)
    }
  };

  const woFetchConfig = {
    ...WO_FETCH_CONFIG,
    action: fetchEntitiesActions.fetchEntities,
  };

  const items = sData?.get('preselectedAssets')?.toJS() ?? [];
  const [item] = items;

  const cassetteFetchConfig = {
    entityType: EntityPropertyTypes.CASSETTE_BUSINESS_ID,
    filter: [FetchEntitiesFilters.ACTIVE],
    action: fetchEntitiesActions.fetchEntities,
    searchBy: {
      cassette: [{
          objectName: 'materialId',
          values: [item?.material?.id]
        },
      ]
    }
  };

  useEffect(() => {
    let initialValues = {
      cassettesList: getInitialCassettes(sData?.get('preselectedAssets')?.toJS()) ?? [{}],
    };

    initialize(initialValues);
  }, []);

  useEffect(() => {
    updateTotalLength();
  }, [fieldsValues]);


  const getDialogButtons = () => {
    return {
      left: [
        {
          id: 'cancel',
          text: dialogLabels.get('cancel'),
          action: actions.hide,
        },
      ],
      right: [
        {
          id: 'submit',
          text: dialogLabels.get('create'),
          bsStyle: 'primary',
          loading: sData.get('loading'),
          action: handleSubmit(onSubmit),
          disabled: pristine || sData.get('loading') ||
                    sData.get('hasError') ||
                    (sData.get('showIgnoreValidationCheckbox') &&
                      !sData.get('isIgnoreValidationWarnings')),
        },
      ],
    };
  }

  const onSubmit = (data) => {
    let submitData = {
      workOrder: data?.workOrder?.assetsValue?.label ?? null,
      sourceCassettes: createSourceCassettes(data.cassettesList ?? []),
      businessId: data?.spoolId,
      spoolStatus: data?.spoolStatus?.value,
      comment: data?.comment ?? '',
      ignoreValidationWarnings:
        sData.get('showIgnoreValidationCheckbox') && data.footerValidationCheckbox
          ? data.footerValidationCheckbox
          : false,
    };

    actions.submit(submitData, null, reloadParentComponent, printPdfLabels);
  };

  const onHide = () => {
    actions.hide();
  };

  const getTotalLength = () => {
    let sum = fieldsValues?.reduce((previous, current) => {
      let cutQuantity = current.length ?? 0;
      return previous + (+(cutQuantity) );
    }, 0) ?? 0;

    return round(sum);
  }

  const updateTotalLength = () => {
    const length = getTotalLength();
    change('length', length);
  }

  const getCassettesItemRenderer = (data) => {
    return (
      <AssetLengthListItem
        {...data}
        fetchConfig={cassetteFetchConfig}
        change={change}
        touch={touch}
        assetType={getEnumValue(enumTypes.OBJECT_TYPE)('CASSETTE')}
      />
    )
  }

  const generateSpoolId = () => {
    actions.generateSpoolId().then((response) => {
      change('spoolId', response.data);
    });
  };

  const getFooterValidationCheckBox = () => {
    return sData.get('showIgnoreValidationCheckbox')
      ? { label: dialogLabels.get('ignorewarnings'), onChange: onFooterValidationChange }
      : null;
  };

  const onFooterValidationChange = (value) => {
    actions.toggleIgnoreValidationWarningsCheckbox(value);
  };

  return (
    <Dialog
      id="create-spool-from-cassettes-dialog"
      className="create-spool-from-cassettes-dialog"
      titleText={labels.get('header.title')}
      footerCheckbox={footerCheckbox}
      show={sData.get('show')}
      onHide={onHide}
      sData={sData}
      footerButtons={getDialogButtons()}
      footerValidationCheckbox={getFooterValidationCheckBox()}
      footerInformationIcon={dialogHelper.getFooterInformationIcon(
        sData,
        dialogLabels
      )}
      onEnterKeyPress={handleSubmit(onSubmit)}
    >
      <InputSection label={labels.get('workOrder')} htmlFor="workOrder" className="work-order-section inline left-side no-margin">
        <EntitiesMultiSelect
          id="workOrder"
          name="workOrder"
          entitiesTypes={WO_ENTITIES_TYPES}
          fetchConfig={woFetchConfig}
          change={change}
          isMulti={false}
          showEntitiesTypes={false}
          autoFocus={true}
        />
      </InputSection>

      <div className='assets-container'>

        <div className='assets-container-left'>
          <div className="list-title">
            <span>{labels.get('cassettes') + '*'}</span>
            <div>
              <span>{labels.get('length')}</span>
              <span className="units">{`(${UnitHelper.getLabelForUnitType(unitTypes.LENGTH)})*`}</span>
            </div>
          </div>

          <List
            id='cassettesList'
            name='cassettesList'
            className='cassettes-list'
            itemRenderer={getCassettesItemRenderer}
            minFieldsCount={1}
            maxItemsToOverflow={6}
            addButtonLabel={labels.get('cassettesList.button.add')}
          />
        </div>

        <label className="add-to-asset-arrow">
          <i className="pl pl-move-arrow-right"></i>
        </label>

        <div className='assets-container-right'>
          <div className="list-title">
            <span>{labels.get('spool') + '*'}</span>
            <div>
              <span>{labels.get('length')}</span>
              <span className="units">{`(${UnitHelper.getLabelForUnitType(unitTypes.LENGTH)})*`}</span>
            </div>
          </div>

          <div className='spool-section'>
            <GenerateButton
              id="spoolId"
              name="spoolId"
              className="spoolId"
              placeholder={labels.get('assetIdPlaceholder')}
              validate={[Validation.required, maxLength100]}
              onClick={generateSpoolId}
              buttonText={dialogLabels.get('generate')}
            />
            <TextField id='length'
                       name='length'
                       className="spool-length"
                       disabled={true}
            />
          </div>

          <InputSection label={labels.get('spoolStatus')} htmlFor="spoolStatus" className="">
            <Combobox
              id="spoolStatus"
              name="spoolStatus"
              options={getStatuses()}
              optionRenderer={getComponentToRender}
            />
          </InputSection>
        </div>

      </div>

      <InputSection label={labels.get('comment')} htmlFor="comment" className="full-width">
        <TextAreaField id="comment-textField" name="comment" className="comment-textField" placeholder={labels.get('comment.placeholder')}/>
      </InputSection>

    </Dialog>
  );
}


CreateSpoolFromCassettesDialog = reduxForm({
  form: 'createSpoolFromCassettesDialog',
  onChange: (values, dispatch, props, previousValues) => {
    dialogHelper.onFormChangeHandler(values, props, previousValues);
  },
})(CreateSpoolFromCassettesDialog);

const selector = formValueSelector('createSpoolFromCassettesDialog');

export default CreateSpoolFromCassettesDialog = connect( state => {
  return {  fieldsValues: selector(state, 'cassettesList') }
})( CreateSpoolFromCassettesDialog );


CreateSpoolFromCassettesDialog.propTypes = {
  actions: PropTypes.object.isRequired,
  fetchEntitiesActions: PropTypes.object.isRequired,
  sData: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func,
  change: PropTypes.func,
  touch: PropTypes.func,
  initialize: PropTypes.func,
  pristine: PropTypes.bool,
  reloadParentComponent: PropTypes.func,
};
